import React, { FC } from 'react';
import { format } from 'date-fns';
import TableWithColumnSelectorFixedHeader, { TableType } from '../../core/TableWithColumnSelectorFixedHeader';
import { useQuery } from '@apollo/client';
import { physicianPatientsAnalyticsQuery } from '../../../graph/dashboard';
import { getMinutes } from './util';
import { PhysicianPatientJourneyTableData } from '../../../types/Analytics';
import { DateRangeType } from '../../../types/utils';
import { useScope } from '../../../hooks/useScope';
import { PacuGroupBy } from './PacuGroupBy';
import { PacuStepNames, usePacuOptionalProcedureSteps } from '../../../hooks/usePacuOptionalProcedureSteps';

const toTableData = (
  data: PhysicianPatientJourneyTableData[],
  names: PacuStepNames,
  hasPostOp?: boolean
): TableType => {
  const postOpInOutHeaders = hasPostOp ? ['POSTOP In', 'POSTOP Out'] : [];
  const postOpHeaders = hasPostOp ? ['POSTOP'] : [];
  const chair = names.transferredToChair;
  const pt = names.transferredToPT;
  const ptComplete = names.ptComplete;
  const baseHeaders = [
    'Date of service',
    'Patient MRN',
    'Patient',
    'OR',
    'Procedure Type',
    'Recovery',
    `Recovery In to ${chair}`,
    `Recovery In to ${pt}`,
    `Recovery In to ${ptComplete}`,
    `${ptComplete} Delay Reason`,
    `${chair} to ${pt}`,
    `${pt} to Recovery Exit`,
    `${pt} to ${ptComplete}`,
    'Recovery In to Discharged',
    'Recovery In',
    chair,
    pt,
    ptComplete,
    'Recovery Out',
    'Discharged At',
  ];
  return {
    configHeader: [...baseHeaders],
    headers: [
      {
        id: 'h1',
        columns: [...baseHeaders],
      },
    ],
    rows: (data || []).map(e => ({
      id: e?.id.toString(),
      columns: [
        e.surgeryTime,
        e.patientProviderId || '',
        e.patient,
        e.operationRoom,
        e.procedureType,
        getMinutes(e.recoveryInMinutes),
        getMinutes(e.pacuInToTransferToChairInMinutes),
        getMinutes(e.pacuInToTransferToPTInMinutes),
        getMinutes(e.pacuInToPTCompleteInMinutes),
        e.ptCompleteDelayReason || '-',
        getMinutes(e.transferToChairToTransferToPTInMinutes),
        getMinutes(e.transferToPTToPacuOutInMinutes),
        getMinutes(e.transferToPTToPTCompleteInMinutes),
        getMinutes(e.pacuInToDischargedInMinutes),
        e.recoveryIn,
        e.transferToChair,
        e.transferToPT,
        e.ptComplete,
        e.recoveryOut,
        e.dischargedAt,
      ],
    })),
  };
};

const PhysicianPacuPatientsAnalytics: FC<{
  physicianId: number;
  physicianName?: string;
  dateRange: DateRangeType;
  filter: any;
  groupBy?: PacuGroupBy;
  entityLabel?: string;
}> = ({ physicianId, physicianName, dateRange, filter, groupBy, entityLabel }) => {
  const { data } = useQuery(physicianPatientsAnalyticsQuery, {
    variables: {
      physicianId,
      filter,
      groupBy,
    },
  });

  const scope = useScope();
  const optionalPacuSteps = usePacuOptionalProcedureSteps();
  const hasPostOp = scope?.hospital?.modules?.hasPostop;
  const table: PhysicianPatientJourneyTableData[] = data?.physicianPatientsAnalytics || [];

  return (
    <>
      <TableWithColumnSelectorFixedHeader
        configName="physician-patients-table-config"
        tableId="Physician-Patients-table"
        tableName={`For ${(entityLabel || 'physician').toLowerCase()} ${physicianName || ''}`}
        excelFileName={`Patient-Journey-${format(dateRange.from, 'MM/DD/YYYY')}-${format(dateRange.to, 'MM/DD/YYYY')}`}
        {...toTableData(table, optionalPacuSteps.names, hasPostOp)}
      />
    </>
  );
};

export default PhysicianPacuPatientsAnalytics;
